import Vue from 'vue'
import Vuex from 'vuex'
import {getMe} from "@/api/me";
import {problemOf} from "@/api/errors";
import {MeDto, PartnerDto} from "@/api/types";

Vue.use(Vuex)

export interface RootState {
    snackbar: boolean;
    snackbarText: string;
    isError: boolean;
    timeout: number;

    /** The logged-in user (`GET /api/me`); `null` before the first load, when logged out and after a 401. */
    user: MeDto | null;
    /** True while a `loadUser` is in flight. Starts true: `App.vue` dispatches `loadUser` as soon as it is created. */
    userLoading: boolean;
}

/** Number of `loadUser` calls in flight, so that `userLoading` only clears when the last one settles. */
let pendingUserLoads = 0;

export default new Vuex.Store<RootState>({
    state: {
        snackbar: false,
        snackbarText: "",
        isError: false,
        timeout: 2000,

        user: null,
        userLoading: true,
    },
    getters: {
        isLoggedIn: (state): boolean => state.user !== null,

        isAdmin: (state): boolean => state.user?.isAdmin ?? false,

        /** The partner of the logged-in user, or `null` (an admin without a partner, or nobody logged in). */
        partner: (state): PartnerDto | null => state.user?.partner ?? null,

        /**
         * True for an admin and for a user with a partner; false when nobody is logged in and for the "account not
         * set up" case (non-admin without a partner). Pages behind that overlay skip their initial load when this is
         * false, because every partner-scoped endpoint would answer 403 `NO_PARTNER`.
         */
        hasAccess: (state, getters): boolean => getters.isAdmin || getters.partner !== null,
    },
    mutations: {
        setSnackbar(state, snackbarText) {
            state.snackbar = true;
            state.isError = false;
            state.snackbarText = snackbarText;
            state.timeout = 2000;
        },

        setError(state, snackbarText) {
            state.snackbar = true;
            state.isError = true;
            state.snackbarText = snackbarText;
            state.timeout = 10000;
        },

        hideSnackbar(state) {
            state.snackbar = false;
        },

        setUser(state, user: MeDto | null) {
            state.user = user;
        },

        setUserLoading(state, loading: boolean) {
            state.userLoading = loading;
        },

        /** Logout, or a 401 on `GET /api/me`: nobody is logged in. */
        resetUser(state) {
            state.user = null;
            state.userLoading = false;
        },
    },
    actions: {
        /**
         * Loads `GET /api/me` into `state.user`. Used on start (`App.vue`), after login and for `reloadUser`.
         *
         * Never rejects: it resolves with the user, or `null` when the call failed. A failure has already been shown
         * by the http interceptor (snackbar, or the redirect to `/login` on a 401), so callers only need to check for `null`.
         * A 401 also clears the user, because the session is gone. Any other failure keeps the user that was loaded before.
         */
        async loadUser({commit}): Promise<MeDto | null> {
            pendingUserLoads++;
            commit("setUserLoading", true);

            try {
                const user = await getMe();
                commit("setUser", user);
                return user;
            } catch (e) {
                if (problemOf(e)?.status === 401) {
                    commit("resetUser");
                }

                return null;
            } finally {
                pendingUserLoads--;
                commit("setUserLoading", pendingUserLoads > 0);
            }
        },
    },
    modules: {}
})
